import React, { useState } from 'react';
import { SimulationConfig, WavePattern, Emitter } from './types';
import { Sliders, Droplets, Pause, Play, RefreshCw, Zap, Repeat, Repeat1, Square, ChevronDown, Volume2, VolumeX } from 'lucide-react';
import { useWavePoolAudio } from '../../hooks/useWavePoolAudio';

interface WavePoolControlsProps {
  config: SimulationConfig;
  emitters: Emitter[];
  onConfigChange: (newConfig: SimulationConfig) => void;
  onPatternSelect: (pattern: WavePattern) => void;
  onReset: () => void;
  onExportToLab?: () => void;
}

const WavePoolControls: React.FC<WavePoolControlsProps> = ({ 
  config, 
  emitters,
  onConfigChange, 
  onPatternSelect, 
  onReset,
  onExportToLab
}) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const { isPlaying: isAudioPlaying, toggleAudio } = useWavePoolAudio(config, emitters);
        const baseFrequency = config.baseFrequency ?? config.frequency;
  
    const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

    const handleChange = (key: keyof SimulationConfig, value: number | boolean) => {
        onConfigChange({ ...config, [key]: value });
    };

    const handleNumericInput = (
        key: keyof SimulationConfig,
        rawValue: string,
        bounds: { min: number; max: number }
    ) => {
        const parsed = parseFloat(rawValue);
        if (Number.isNaN(parsed)) return;
        handleChange(key, clamp(parsed, bounds.min, bounds.max));
    };

    return (
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-5 text-slate-800 shadow-md">
      
      {/* Header */}
            <div className="flex items-center justify-between pb-4 border-b border-slate-200">
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="flex items-center gap-2 hover:text-indigo-600 transition-colors"
                >
                    <Sliders size={16} className="text-indigo-500" />
                    <h2 className="text-sm font-semibold text-slate-900">Pool Controls</h2>
                    <ChevronDown 
                        size={16} 
                        className={`text-slate-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    />
                </button>
        <div className="flex items-center gap-2">
            <button
                onClick={toggleAudio}
                title={isAudioPlaying ? "Mute Audio" : "Play Audio"}
                className={`p-2 rounded-lg transition-colors text-xs ${isAudioPlaying ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
            >
                {isAudioPlaying ? <Volume2 size={16} /> : <VolumeX size={16} />}
            </button>
            <button
                onClick={onReset}
                title="Stop & Reset"
                                className="p-2 rounded-lg transition-colors bg-slate-100 text-slate-600 hover:bg-rose-100 hover:text-rose-600 text-xs"
            >
                <Square size={16} fill="currentColor" />
            </button>
            <button 
                onClick={() => onConfigChange({ ...config, autoPlay: !config.autoPlay })}
                title={config.autoPlay ? "Pause" : "Play"}
                                className={`p-2 rounded-lg transition-colors text-xs ${config.autoPlay ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
            >
                {config.autoPlay ? <Pause size={16} /> : <Play size={16} />}
            </button>
        </div>
      </div>

      {isExpanded && (
      <>
      {/* Sliders */}
      <div className="space-y-4">
        <div className="space-y-2">
            <div className="flex items-center justify-between text-xs font-mono uppercase text-slate-500">
                <span>Wave Amplitude</span>
                <input
                    type="number"
                    min="0.1"
                    max="2.0"
                    step="0.1"
                    value={config.amplitude}
                    onChange={(e) => handleNumericInput('amplitude', e.target.value, { min: 0.1, max: 2 })}
                    className="w-20 text-right rounded-md border border-slate-200 bg-white text-slate-800 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
            </div>
            <input 
                type="range" min="0.1" max="2.0" step="0.1"
                value={config.amplitude}
                onChange={(e) => handleChange('amplitude', parseFloat(e.target.value))}
                className="w-full accent-indigo-500 h-1.5 bg-slate-100 rounded-lg appearance-none cursor-pointer"
            />
        </div>

        <div className="space-y-2">
            <div className="flex items-center justify-between text-xs font-mono uppercase text-slate-500">
                <span>Base Frequency (Carrier)</span>
                <input
                    type="number"
                    min="0.01"
                    max="0.5"
                    step="0.01"
                    value={baseFrequency}
                    onChange={(e) => handleNumericInput('baseFrequency', e.target.value, { min: 0.01, max: 0.5 })}
                    className="w-20 text-right rounded-md border border-slate-200 bg-white text-slate-800 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
            </div>
            <input 
                type="range" min="0.01" max="0.5" step="0.01"
                value={baseFrequency}
                onChange={(e) => handleChange('baseFrequency', parseFloat(e.target.value))}
                className="w-full accent-indigo-500 h-1.5 bg-slate-100 rounded-lg appearance-none cursor-pointer"
            />
        </div>

        <div className="space-y-2">
            <div className="flex items-center justify-between text-xs font-mono uppercase text-slate-500">
                <span>Frequency (Speed)</span>
                <input
                    type="number"
                    min="0.01"
                    max="0.5"
                    step="0.01"
                    value={config.frequency}
                    onChange={(e) => handleNumericInput('frequency', e.target.value, { min: 0.01, max: 0.5 })}
                    className="w-20 text-right rounded-md border border-slate-200 bg-white text-slate-800 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
            </div>
            <input 
                type="range" min="0.01" max="0.5" step="0.01"
                value={config.frequency}
                onChange={(e) => handleChange('frequency', parseFloat(e.target.value))}
                className="w-full accent-indigo-500 h-1.5 bg-slate-100 rounded-lg appearance-none cursor-pointer"
            />
        </div>

        <div className="space-y-2">
            <div className="flex items-center justify-between text-xs font-mono uppercase text-slate-500">
                <span>Damping (Viscosity)</span>
                <input
                    type="number"
                    min="0.9"
                    max="0.999"
                    step="0.001"
                    value={config.damping}
                    onChange={(e) => handleNumericInput('damping', e.target.value, { min: 0.9, max: 0.999 })}
                    className="w-20 text-right rounded-md border border-slate-200 bg-white text-slate-800 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
            </div>
            <input 
                type="range" min="0.900" max="0.999" step="0.001"
                value={config.damping}
                onChange={(e) => handleChange('damping', parseFloat(e.target.value))}
                className="w-full accent-indigo-500 h-1.5 bg-slate-100 rounded-lg appearance-none cursor-pointer"
            />
        </div>

      </div>

      {/* Pattern Grid */}
        <div className="space-y-3 pt-2">
        <span className="text-xs font-mono uppercase text-slate-500 block">Wave Patterns</span>
        <div className="grid grid-cols-2 gap-2">
            {[
                { id: WavePattern.MANUAL, label: 'Manual', icon: <Zap size={13}/> },
                { id: WavePattern.SINGLE_DROP, label: 'Pulse', icon: <Droplets size={13}/> },
                { id: WavePattern.CONCENTRIC, label: 'Concentric', icon: <RefreshCw size={13}/> },
                { id: WavePattern.CHAOS, label: 'Chaos', icon: <Zap size={13}/> },
            ].map(p => (
                <button
                    key={p.id}
                    onClick={() => onPatternSelect(p.id)}
                    className={`
                        text-xs flex items-center justify-center gap-1 py-2.5 px-2 rounded-lg border transition-all font-medium
                        ${config.pattern === p.id 
                            ? 'bg-indigo-600 text-white border-indigo-500 shadow-lg shadow-indigo-500/20' 
                            : 'bg-slate-50 border-slate-200 hover:bg-white text-slate-700'}
                    `}
                >
                    {p.icon}
                    <span>{p.label}</span>
                </button>
            ))}
        </div>
      </div>
      </>
      )}

      {/* Toggles - Always visible */}
      <div className="pt-2 border-t border-slate-200 space-y-3">
         <label className="flex items-center justify-between cursor-pointer group">
            <div className="flex items-center gap-2 text-xs text-slate-600 group-hover:text-slate-800 transition-colors">
                 {config.loop ? <Repeat size={14} /> : <Repeat1 size={14} />}
                 <span>Loop Animation</span>
            </div>
            <div 
                onClick={() => handleChange('loop', !config.loop)}
                className={`w-9 h-5 rounded-full relative transition-colors ${config.loop ? 'bg-indigo-600' : 'bg-slate-300'}`}
            >
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform duration-200 ${config.loop ? 'left-4' : 'left-0.5'}`} />
            </div>
         </label>

         <label className="flex items-center justify-between cursor-pointer group">
            <span className="text-xs text-slate-600 group-hover:text-slate-800 transition-colors">Rain Mode</span>
            <div 
                onClick={() => handleChange('rainMode', !config.rainMode)}
                className={`w-9 h-5 rounded-full relative transition-colors ${config.rainMode ? 'bg-indigo-600' : 'bg-slate-300'}`}
            >
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform duration-200 ${config.rainMode ? 'left-4' : 'left-0.5'}`} />
            </div>
         </label>
      </div>

      {/* Export to Wave Lab Button */}
      {onExportToLab && (
        <div className="pt-2 border-t border-slate-200">
          <button 
            onClick={onExportToLab}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-4 py-3 rounded-lg text-sm font-bold shadow-lg transition-all hover:scale-105 active:scale-95 hover:shadow-xl"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span>Open in Wave Lab</span>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </button>
        </div>
      )}

    </div>
  );
};

export default WavePoolControls;
